<script>
import { preventDefault } from "svelte/legacy";
import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
import {
    modalState,
    showModal,
    hideModal,
} from "@app/store/modalStore.svelte.js";
import { uiState } from "@app/store/uiStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import {
    resetMarkerStateWithData
} from "@app/store/markerStore.svelte.js";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import {
    resetHistory, insertHistoryAction
} from "@app/store/historyStore.svelte.js";
import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import frameRates from "@cclib/dict/frameRates.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import { tick} from "svelte";
import { fade } from "svelte/transition";

import { Circle } from "svelte-loading-spinners";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import firebase from "@app/configs/firebase.js";
import * as m from '@app/paraglide/messages.js';

/* Default Settings */
let files = $state(),
    statusMsg = $state(""),
    showOptions = $state(false),
    importEventGroups = $state(true),
    importMetadata = $state(true),
    importSpeakers = $state(true),
    importMarkers = $state(true),
    mergeImport = $state(false),
    importing = $state(false);

function convertProjectToV3(projectJson) {
    let projectConvert = {
        ...new _Project({
            type: "default",
            name: projectJson.projectSettings.title,
            description: m.project_converter_description(),
            created: Date.now(),
            frameRate: parseFloat(
                projectJson.projectSettings.framerate || 29.97,
            ),
            dropFrame:
                frameRates.dropFrameMap[
                    parseFloat(projectJson.projectSettings.framerate || 29.97)
                ],
            userName: firebase.auth().currentUser.email,
            ownerId: firebase.auth().currentUser.uid,
        }),
    };

    let events = [];
    projectJson.captions.forEach((caption) => {
        try {
            let start = caption.incodeSec || 0;
            let end = caption.outcodeSec || 0;
            let style = caption.style || "Pop-On";
            let alignment = "center";
            let xPos = "center";
            let yPos = "end";
            let text;
            if (caption.primaryLang[0].captionText) {
                text = convertToHtml(
                    caption.primaryLang
                        .map((line) => {
                            let prefix = "",
                                suffix = "";
                            prefix += line.bold ? "<b>" : "";
                            suffix = line.bold ? "<b>" + suffix : suffix;
                            prefix += line.italics ? "<i>" : "";
                            suffix = line.italics ? "<i>" + suffix : suffix;
                            prefix += line.underline ? "<u>" : "";
                            suffix = line.underline ? "<u>" + suffix : suffix;
                            return prefix + line.captionText + suffix;
                        })
                        .join("\n"),
                );

                if (caption.primaryLang[0].positionY < 8) {
                    yPos = "start";
                }
            } else {
                text = convertToHtml(caption.subtitleText);
            }

            events.push({
                ...new _Event({
                    start: start,
                    end: end,
                    style: style,
                    text: text,
                    alignment: alignment,
                    xPos: xPos,
                    yPos: yPos,
                }),
            });
        } catch (err) {
            console.log(err, err.message);
        }
    });

    projectConvert.eventGroups = [
        {
            ...new _EventGroup({
                name: projectConvert.name,
                type: "subtitle",
                events: events,
            }),
        },
    ];

    if (projectJson.transcription && projectJson.transcription.text) {
        let transcriptionEvent = {
            ...new _Event({
                text: convertToHtml(projectJson.transcription.text),
            }),
        };

        projectConvert.eventGroups.push({
            ...new _EventGroup({
                name: projectConvert.name + " (transcript)",
                type: "transcription",
                events: [transcriptionEvent],
            }),
        });
    }

    return projectConvert;
}

async function importProjectFile() {
    try {
        importing = true;
        if (!files || !files[0]) {
            toast.push(m.error_no_project_file_selected(), {
                classes: ["toast-danger"],
            });
            importing = false;
            return;
        }

        statusMsg = m.reading_file_contents();
        toast.push(m.reading_file_contents(), {
            classes: ["toast-warning"],
        });

        uiState.timeline = false;
        projectState.selected = false;
        await tick();

        let reader = new FileReader();
        reader.onload = function (e) {
            statusMsg = m.importing_contents();
            let projectJson = JSON.parse(e.target.result);
            if (projectJson.version !== 3) {
                projectJson = convertProjectToV3(projectJson);
            }

            if (mergeImport) {
                if (importEventGroups) {
                    let numberOfEventGroups = eventGroupState.length;
                    projectJson.eventGroups.forEach(
                        (eventGroup, eventGroupIndex) => {
                            // Use push instead of direct assignment
                            eventGroupState.push({
                                ...new _EventGroup({
                                    ...eventGroup,
                                    ...{
                                        events: [],
                                    },
                                }),
                            });

                            eventGroup.events.forEach((event) => {
                                // Store the result of insertEvent and update properties manually
                                const result = insertEvent(
                                    eventGroupState[
                                        eventGroupIndex + numberOfEventGroups
                                    ],
                                    event,
                                );

                                // Copy events from result to the existing event group
                                eventGroupState[
                                    eventGroupIndex + numberOfEventGroups
                                ].events = [...result.events];

                                // Copy selected property if it exists
                                if (result.selected) {
                                    eventGroupState[
                                        eventGroupIndex + numberOfEventGroups
                                    ].selected = [...result.selected];
                                }
                            });
                        },
                    );
                }

                if (importMetadata) {
                    projectJson.metadata.forEach((metadataInfo) => {
                        metadataState.push(metadataInfo);
                    });
                }

                if (importSpeakers) {
                    projectJson.speakers.forEach((speakerInfo) => {
                        speakerState.push(speakerInfo);
                    });
                }

                if (importMarkers) {
                    resetMarkerStateWithData(projectJson.markers);
                }

                if (eventGroupState.length > 0) {
                    projectState.selected = eventGroupState.length-1;
                }

                resetHistory();
                insertHistoryAction({
                    name: "import project", //action name
                    eventGroup: projectState.selected,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify(eventGroupState),
                        }
                    ],
                });
                hideModal();
            } else {
                setExternalDataState({
                    platform: "Project File",
                    status: "importing",
                    rawDescription: JSON.stringify(projectJson),
                });

                showModal("loadProjectFile");
                return;
            }
        };

        reader.readAsText(files[0]);
    } catch (err) {
        console.log(err);
        importing = false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'projectImport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    tabindex="-1"
    id="ProjectImportModal"
    aria-labelledby="projectImportTitle"
    aria-describedby="projectImportDescription">
    <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="projectImportTitle">
                    {m.project_import()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closeProjectImport"
                    aria-label={m.close_project_import_modal()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div id="projectImportDescription" class="visually-hidden">
                    {m.project_import_description()}
                </div>
                <div class="row">
                    <div class="col">
                        <form onsubmit={preventDefault(importProjectFile)}>
                            <div class="mb-3 shadow bg-secondary rounded p-3">
                                <label
                                    class="form-label"
                                    for="projectFilePicker"
                                    >{m.select_project_file()} <i
                                        class="bi bi-info-circle"
                                        title={m.supported_format_ccprj()}></i
                                    ></label>
                                <input
                                    bind:files
                                    type="file"
                                    class="form-control"
                                    id="projectFilePicker"
                                    accept=".ccprj" />
                            </div>
                            <button
                                type="button"
                                id="showMoreOptions"
                                class="btn btn-outline-warning mb-2 dropdown-toggle"
                                aria-expanded={showOptions}
                                onclick={() => {
                                    showOptions = !showOptions;
                                }}>
                                {m.more_options()}
                            </button>
                            {#if showOptions}
                                <div
                                    id="advancedOptions"
                                    class="p-3 bg-secondary shadow rounded">
                                    <div class="mb-3">
                                        <div class="form-check">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                bind:checked={mergeImport}
                                                id="mergeImportOption" />
                                            <label
                                                class="form-check-label"
                                                for="mergeImportOption">
                                                {m.merge_import()} <i
                                                    class="bi bi-info-circle"
                                                    title={m.merge_import_help()}
                                                ></i>
                                            </label>
                                        </div>
                                    </div>
                                    {#if mergeImport}
                                        <hr />
                                        <div class="row">
                                            <!-- Event Groups -->
                                            <div class="mb-3 col-3">
                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        value=""
                                                        bind:checked={
                                                            importEventGroups
                                                        }
                                                        id="importEventGroupsOption" />
                                                    <label
                                                        class="form-check-label"
                                                        for="importEventGroupsOption">
                                                        {m.event_groups()}
                                                    </label>
                                                </div>
                                            </div>
                                            <!-- Speakers -->
                                            <div class="mb-3 col-3">
                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        value=""
                                                        bind:checked={
                                                            importSpeakers
                                                        }
                                                        id="importSpeakersOption" />
                                                    <label
                                                        class="form-check-label"
                                                        for="importSpeakersOption">
                                                        {m.project_speakers()}
                                                    </label>
                                                </div>
                                            </div>
                                            <!-- markers -->
                                            <div class="mb-3 col-3">
                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        bind:checked={
                                                            importMarkers
                                                        }
                                                        id="importMarkerOption" />
                                                    <label
                                                        class="form-check-label"
                                                        for="importMarkerOption">
                                                        {m.project_markers()}
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="mb-3 col-3">
                                                <div class="form-check">
                                                    <input
                                                        class="form-check-input"
                                                        type="checkbox"
                                                        bind:checked={
                                                            importMetadata
                                                        }
                                                        id="importMetdataOption" />
                                                    <label
                                                        class="form-check-label"
                                                        for="importMetdataOption">
                                                        {m.project_metadata()}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    {/if}
                                </div>
                            {/if}
                        </form>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                {#if importing}
                    <p
                        class="text-muted small"
                        role="status"
                        aria-live="polite">
                        {m.project_import_status({ status: statusMsg })}
                    </p>
                    <Circle size="30" color="#1eb4b2" unit="px" duration="1s"
                    ></Circle>
                {/if}
                <button
                    disabled={importing || !files}
                    class="btn btn-primary"
                    type="button"
                    id="importProjectBtn"
                    onclick={importProjectFile}
                    aria-disabled={importing || !files}>
                    {m.project_import_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#advancedOptions {
    overflow-y: auto;
    overflow-x: none;
    max-height: 50vh;
}
</style>
